<script setup>
import { computed } from "vue";
import DropdownStati from "@/Components/DropdownStati.vue";

const props = defineProps({
  appointment: Object,
  timeslotType: String,
  filterView: String, // filterView: tag_behandler, tag_raum, woche_behandler
  concurrentCount: Number,
  hideNames: Boolean,
});

const patient = computed(() => {
  let appointment = props.appointment;
  if (appointment) {
    if (appointment.patient) {
      // registered patient
      return appointment.patient;
    } else {
      // custom patient
      return appointment.patient_custom;
    }
  }
  return null;
});

function isColorTooDark(c) {
  c = c.substring(1); // strip #
  let rgb = parseInt(c, 16); // convert rrggbb to decimal
  let r = (rgb >> 16) & 0xff; // extract red
  let g = (rgb >> 8) & 0xff; // extract green
  let b = (rgb >> 0) & 0xff; // extract blue
  let luma = 0.2126 * r + 0.7152 * g + 0.0722 * b; // per ITU-R BT.709
  if (luma < 100) {
    return "#fff";
  } else {
    return "#000";
  }
}
</script>

<template>
  <div
    class="oteo-appointment"
    :class="{
      'oteo-appointment-start': props.timeslotType == 'start',
      'oteo-appointment-middle': props.timeslotType == 'middle',
      'oteo-appointment-end': props.timeslotType == 'end',
      'oteo-appointment-full': props.timeslotType == 'full',
      'oteo-appointment-past': new Date(props.appointment.end) < new Date(),
    }"
    :style="`background-color: ${
      props.appointment.practitioner.color
    }; color: ${isColorTooDark(props.appointment.practitioner.color)};`"
    :title="
      (!props.hideNames
        ? (patient.patient_number ? '(' + patient.patient_number + ')' : '') +
          ' ' +
          patient.lastname +
          ', ' +
          patient.firstname
        : 'Inkognito-Modus aktiviert') +
      (props.appointment.type
        ? ' - Behandlung: ' +
          props.appointment.type.name +
          (props.appointment.series_id ? '+' : '')
        : '') +
      (props.appointment.room
        ? ' - Raum: ' + props.appointment.room.name
        : '') +
      (props.appointment.description
        ? ' - Anmerkung: ' + props.appointment.description
        : '')
    "
  >
    <div
      v-if="props.timeslotType == 'start' || props.timeslotType == 'full'"
      class="flex w-full"
    >
      <div
        v-if="concurrentCount <= 2"
        class="truncate grow"
        :class="{ 'blur-sm': props.hideNames }"
      >
        {{
          !props.hideNames
            ? (patient.patient_number
                ? "(" + patient.patient_number + ")"
                : "") +
              " " +
              patient.lastname +
              ", " +
              patient.firstname
            : "Inkognito-Modus aktiviert"
        }}
      </div>

      <div v-else>
        {{ props.appointment.type.abbreviation
        }}<span v-if="props.appointment.series_id">+</span>
      </div>

      <div class="flex">
        <!-- treatment -->
        <div
          v-if="concurrentCount <= 2"
          class="appointment-tag"
          :title="
            props.appointment.type.name +
            (props.appointment.series_id ? ' - Terminserie' : '')
          "
          :style="`background-color: ${
            props.appointment.type.color
          }; color: ${isColorTooDark(props.appointment.type.color)};`"
        >
          {{ props.appointment.type.abbreviation
          }}<span v-if="props.appointment.series_id">+</span>
        </div>

        <!-- room -->
        <div
          v-if="
            props.filterView != 'tag_raum' &&
            props.appointment.room &&
            concurrentCount == 1
          "
          class="appointment-tag"
          :title="props.appointment.room.name"
          :style="`color: ${isColorTooDark(
            props.appointment.practitioner.color
          )};`"
        >
          {{ props.appointment.room.number }}
        </div>

        <!-- practitioner -->
        <div
          v-if="
            props.filterView != 'tag_behandler' &&
            props.appointment.practitioner &&
            concurrentCount == 1
          "
          class="appointment-tag"
          :title="props.appointment.practitioner.fullname"
          :style="`color: ${isColorTooDark(
            props.appointment.practitioner.color
          )};`"
        >
          {{ props.appointment.practitioner.username }}
        </div>

        <DropdownStati
          v-if="concurrentCount == 1"
          v-model:stati="props.appointment.stati"
          v-model:status="props.appointment.status"
          v-model:appointment="props.appointment"
        ></DropdownStati>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
$border-radius: 4px;

.oteo-appointment {
  display: flex;
  align-items: center;
  font-size: 12px;
  cursor: pointer;

  margin-left: 1px;
  margin-right: 1px;
  padding-left: 2px;
  //padding-right: 1px;

  &.oteo-appointment-start {
    height: calc(100% - 2px);
    margin-top: 2px;
    padding-bottom: 2px;
    border-top-left-radius: $border-radius;
    border-top-right-radius: $border-radius;
  }

  &.oteo-appointment-middle {
    height: calc(100% + 1px);
    margin-top: -1px;
    border-radius: 0;
  }

  &.oteo-appointment-end {
    height: calc(100% - 1px);
    margin-top: -1px;
    border-bottom-left-radius: $border-radius;
    border-bottom-right-radius: $border-radius;
  }

  &.oteo-appointment-full {
    height: calc(100% - 4px);
    margin-top: 2px;
    border-radius: $border-radius;
  }

  &.oteo-appointment-past {
    opacity: 0.5;
  }
}
</style>
